import {
  Component,
  computed,
  effect,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { Location } from '@angular/common';
import { HomeIconComponent } from './icons/home-icon/home-icon.component';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { jwtDecode } from 'jwt-decode';
import { HttpClient } from '@angular/common/http';
import { map, retry } from 'rxjs';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { AppTileIconComponent } from './icons/app-tile-icon/app-tile-icon.component';
import {
  ApplicationData,
  ApplicationEnumValues,
  deDuplicateApplication,
  getApplicationData,
} from './application-data';
import { toSignal } from '@angular/core/rxjs-interop';
import { deleteAllCookies, getCookie, setCookie } from './authUtils';

export interface Claims {
  sub: string;
  userStatus: string;
  portalRole: string;
  iss: string;
  userId: string;
  client_id: string;
  origin_jti: string;
  event_id: string;
  assumedUser: string;
  token_use: string;
  scope: string;
  auth_time: number;
  userType: string;
  customerId?: string;
  customerName?: string;
  customerXPRCodes?: string; // json array of strings
  exp: number;
  iat: number;
  jti: string;
  isImpersonating?: string;
  applicationRoles: string; // json array of strings appname:roll
  username: string;
}
export interface User {
  id: string;
  type: string;
  status: string;
  email: string;
  firstName: string;
  lastName: string;
  notes?: string | null;
  customerId?: string | null;
  portalRole: string;
  groups?:
    | {
        applications?:
          | {
              assignedApplication?: {
                name: ApplicationEnumValues;
                friendlyName?: string | null;
                description?: string | null;
                url?: string | null;
              } | null;
            }[]
          | null;
      }[]
    | null;
  customer?: { __typename?: 'Customer'; name: string } | null;
}

export const COOKIE = 'portal';
export interface TopNavBreadcrumb {
  label: string;
  routerLink: string;
}
//This component will be shred with many apps and handles Portal Auth.
@Component({
  selector: 't-transflo-top-nav',
  standalone: true,
  imports: [
    HomeIconComponent,
    CommonModule,
    RouterModule,
    OverlayPanelModule,
    ButtonModule,
    TooltipModule,
    AppTileIconComponent,
  ],
  templateUrl: './transflo-top-nav.component.html',
  styleUrl: './transflo-top-nav.component.css',
})
export class TransfloTopNavComponent implements OnInit {
  httpClient = inject(HttpClient);
  router = inject(Router);
  location = inject(Location);

  homeHref = input.required<string>();
  portalApiBaseUrl = input.required<string>();
  portalAppUrl = input.required<string>();
  applicationLinkOverrides =
    input<Partial<Record<ApplicationEnumValues, string>>>();
  breadcrumbs = input<TopNavBreadcrumb[]>();
  onClickSidebarMenuIconButton = input<() => void>();

  //Should only be user by portal
  impersonationToken = input<string | undefined>();
  localOverride = input<
    { accessToken: string; refreshToken: string } | undefined
  >();

  logout = output();
  accessTokenVerified = output<{ accessToken: string; refreshToken: string }>();

  readonly user = signal<User | undefined>(undefined);
  readonly claims = signal<Claims | undefined>(undefined);
  readonly accessToken = signal<string | undefined>(undefined);
  readonly refreshTokenSignal = signal<string | undefined>(undefined);
  readonly sessionExpireTime = computed(() => {
    return this.claims()?.exp
      ? new Date((this.claims()?.exp || 0) * 1000).toLocaleTimeString()
      : '';
  });

  readonly applications = signal<ApplicationData[] | undefined>(undefined);
  readonly initials = computed(() => {
    return this.user()
      ? `${this.user()?.firstName?.charAt(0).toUpperCase()}${this.user()
          ?.lastName?.charAt(0)
          .toUpperCase()}`
      : '\u2014';
  });

  readonly userApplications = computed(() => {
    const userApps = deDuplicateApplication(
      this.claims()?.applicationRoles || ''
    )
      .map(app => this.applications()?.find(a => a.name === app))
      .filter((a): a is NonNullable<typeof a> => !!a);
    return getApplicationData(userApps);
  });

  constructor() {
    effect(() => {
      const impersonationToken = this.impersonationToken();
      if (impersonationToken) {
        this.updateAccessTokenCookie(impersonationToken);
      }
    });
  }

  activeHref = toSignal(
    this.router.events.pipe(
      map(val => {
        if (val instanceof NavigationEnd) return this.location.path(true);
        return '';
      })
    )
  );
  selectedApp = computed(() => {
    return this.userApplications().find(
      app =>
        window.location.href?.includes(app.url || 'false') ||
        this.activeHref()?.includes(app.routerLink || 'false')
    );
  });

  ngOnInit(): void {
    const localTokens = this.localOverride();
    if (localTokens) {
      this.updateAllTokensCookie(
        localTokens.accessToken,
        localTokens.refreshToken
      );
    } else {
      this.initiateToken();
    }
  }

  isSubmittingUnImpersonate = signal(false);
  handleClickUnImpersonateUser() {
    this.isSubmittingUnImpersonate.set(true);
    this.unImpersonate(
      this.accessToken() || '',
      this.refreshTokenSignal() || ''
    ).subscribe({
      next: data => {
        this.isSubmittingUnImpersonate.set(false);
        if (!data) {
          window.alert('Failed to unimpersonate, try again');
        } else {
          this.updateAccessTokenCookie(data);
          window.location.href = `${this.portalAppUrl()}`;
          if (this.activeHref()?.includes(this.portalAppUrl())) {
            window.location.reload();
          }
        }
      },
      error: () => {
        window.alert('Failed to unimpersonate, try again');
      },
    });
  }
  handleLogout() {
    const logoutLogic = () => {
      this.logoutApi(this.accessToken() || '').subscribe({
        next: () => (location.href = `${this.portalAppUrl()}/login`),
        error: () => (location.href = `${this.portalAppUrl()}/login`),
      });
    };

    this.logout.emit();
    deleteAllCookies();
    localStorage.clear();
    //We want to chain these calls so that reloading the page on login doesn't cancel them
    if (this.claims()?.isImpersonating !== 'false') {
      this.unImpersonate(
        this.accessToken() || '',
        this.refreshTokenSignal() || ''
      ).subscribe({
        next: logoutLogic,
        error: logoutLogic,
      });
    } else {
      logoutLogic();
    }
  }

  //This function initiates the auth and refresh logic for all applications relying on the portal token
  private initiateToken() {
    const transfloCookieString = getCookie(COOKIE);
    if (!transfloCookieString) {
      this.handleLogout();
      return;
    }
    const transfloTokens: unknown = JSON.parse(transfloCookieString);
    if (
      !this.hasProps(transfloTokens, [
        'accessToken',
        'refreshToken',
      ] as const) ||
      !transfloTokens.accessToken ||
      !transfloTokens.refreshToken
    ) {
      this.handleLogout();
      return;
    }

    const accessToken = transfloTokens.accessToken as string;
    const refreshToken = transfloTokens.refreshToken as string;

    const claims: Claims = jwtDecode(accessToken);

    this.claims.set(claims);
    this.accessToken.set(accessToken);
    this.refreshTokenSignal.set(refreshToken);

    //expired
    if (Date.now() >= claims.exp * 1000) {
      this.refreshToken(claims.username, refreshToken);
      return;
    }

    //Acts as a ping and to get most upto date user data
    this.getUserData(accessToken, claims.userId)
      .pipe(retry(2))
      .subscribe({
        next: data => {
          this.user.set(data);
          this.accessTokenVerified.emit({ accessToken, refreshToken });
          this.initiateRefreshTimeout({
            accessToken,
            refreshToken,
            email: data.email,
          });
        },
        error: error => {
          if (error.status === 401) {
            this.handleLogout();
          }
        },
      });
    this.getApplications(accessToken)
      .pipe(retry(2))
      .subscribe({
        next: data => {
          this.applications.set(data);
        },
      });
  }

  //This is ugly but is better then forcing a large peer dependency for like apollo-client
  private getUserData(accessToken: string, userId: string) {
    const query = `
      query UserBasic($id: UUID!) {
        user(input: { id: $id }) {
          id
          type
          status
          email
          firstName
          lastName
          notes
          customerId
          groups {
            applications {
              assignedApplication{
                name
                friendlyName
                description
                url
              }
            }
          }
          customer {
            name
          }
          portalRole
        }
      }
    `;
    const variables = { id: userId };
    return this.httpClient
      .post<{ data: { user: User } }>(
        this.portalApiBaseUrl(),
        JSON.stringify({ query, variables }),
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer: ${accessToken}`,
          },
        }
      )
      .pipe(map(d => d.data.user));
  }

  private getApplications(accessToken: string) {
    const query = `
      query Applications {
        applications {
          name
          friendlyName
          description
          url
        }
      }
    `;
    return this.httpClient
      .post<{ data: { applications: ApplicationData[] } }>(
        this.portalApiBaseUrl(),
        JSON.stringify({ query }),
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer: ${accessToken}`,
          },
        }
      )
      .pipe(map(d => d.data.applications));
  }

  private unImpersonate(accessToken: string, refreshToken: string) {
    const variables = { input: { refreshToken } };
    const query = `
        mutation UnImpersonate($input: UnImpersonateInput!) {
          unImpersonate(input: $input) {
            accessToken
          }
        }
    `;
    return this.httpClient
      .post<{ data: { unImpersonate: { accessToken: string } } }>(
        this.portalApiBaseUrl(),
        JSON.stringify({ query, variables }),
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer: ${accessToken}`,
          },
        }
      )
      .pipe(map(d => d.data.unImpersonate.accessToken));
  }
  private logoutApi(accessToken: string) {
    const query = `
        mutation Logout { logout }
    `;
    return this.httpClient.post<{ data: { logout: boolean } }>(
      this.portalApiBaseUrl(),
      JSON.stringify({ query }),
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer: ${accessToken}`,
        },
      }
    );
  }

  private initiateRefreshTimeout({
    accessToken,
    refreshToken,
    email,
  }: {
    accessToken: string;
    refreshToken: string;
    email: string;
  }) {
    const claims: Claims = jwtDecode(accessToken);
    const twoMinutes = 2 * 60 * 1000;
    const milSecondsTillRefreshWindow =
      claims.exp * 1000 - twoMinutes - Date.now();

    if (milSecondsTillRefreshWindow <= 0) {
      this.refreshToken(email, refreshToken);
    }
    setTimeout(() => {
      this.refreshToken(email, refreshToken);
    }, milSecondsTillRefreshWindow);
  }

  private refreshToken(email: string, refreshToken: string) {
    const query = `
        mutation Refresh($email: String!, $refreshToken: String!) {
          refresh(input: { email: $email, refreshToken: $refreshToken }) {
            accessToken
          }
      }
    `;
    const variables = { email, refreshToken };
    return this.httpClient
      .post<{
        data: { refresh: { accessToken: string } } | null;
        errors: unknown | null;
      }>(this.portalApiBaseUrl(), JSON.stringify({ query, variables }), {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
        },
      })
      .subscribe({
        next: data => {
          if (data.errors) {
            this.handleLogout();
          }
          const newToken = data.data?.refresh.accessToken;
          //the refresh token is always the same in cognito
          const newCookieValue = { accessToken: newToken, refreshToken };
          setCookie(COOKIE, JSON.stringify(newCookieValue), 5);
          this.initiateToken();
        },
      });
  }
  private updateAllTokensCookie = (
    accessToken: string,
    refreshToken: string
  ) => {
    if (accessToken && refreshToken) {
      const impersonatedCookie = { refreshToken, accessToken };
      setCookie(COOKIE, JSON.stringify(impersonatedCookie), 5);
      this.initiateToken();
    }
  };

  private updateAccessTokenCookie = (newToken: string) => {
    const transfloCookieString = getCookie(COOKIE);
    const transfloTokens: unknown = JSON.parse(transfloCookieString);
    if (
      this.hasProps(transfloTokens, ['accessToken', 'refreshToken'] as const) &&
      transfloTokens.accessToken &&
      transfloTokens.refreshToken
    ) {
      const impersonatedCookie = {
        refreshToken: transfloTokens.refreshToken,
        accessToken: newToken,
      };
      setCookie(COOKIE, JSON.stringify(impersonatedCookie), 5);
      this.initiateToken();
    }
  };

  private hasProps<T extends readonly string[]>(
    obj: unknown,
    props: T
  ): obj is Record<T[number], unknown> {
    return (
      typeof obj === 'object' && obj !== null && props.every(p => p in obj)
    );
  }
}
